import React, { useEffect, useRef, useState } from 'react';
import { Alert } from '../Alert/Alert';
import { Link, useLocation } from 'react-router-dom';
import { useOrganizationContext } from '../../context/OrganizationContext';
import './RoutineAlert.scss';
import useSWR from 'swr';
import { RoutineAlertDto } from '../../types/Fisiofit/routine';
import { useAuthContext } from '../../context/AuthContext';
import { useClient } from '../../hooks/useClient';

/**
 * How often an open tab asks again for the routine alert. Once a minute (founders' decision, 2026-10-24): a new
 * alert can show up to a minute late, and the route gets 12 times fewer requests than with the old 5 s poll.
 */
const ROUTINE_ALERT_REFRESH_INTERVAL_MS = 60_000;

export const RoutineAlert = () => {
  const { organization } = useOrganizationContext();

  const { get } = useClient();
  const [hiddenRoutinesEndingSoon, setHiddenRoutinesEndingSoon] = useState(false);
  const [hiddenPatientsWithoutRoutine, setHiddenPatientsWithoutRoutine] = useState(false);
  const { user } = useAuthContext();
  const isRoutinePage = useLocation().pathname.includes('/routines');
  const previousNumberOfRoutinesEndingSoonRef = useRef<number>(0);

  const { data } = useSWR(user ? `/users/${user.id}/routine-alert` : null, get<RoutineAlertDto>, {
    refreshInterval: ROUTINE_ALERT_REFRESH_INTERVAL_MS,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });

  useEffect(() => {
    if (data) {
      if (data.numberOfRoutinesEndingSoon !== previousNumberOfRoutinesEndingSoonRef.current) {
        setHiddenRoutinesEndingSoon(false);
      }
      previousNumberOfRoutinesEndingSoonRef.current = data.numberOfRoutinesEndingSoon;
    }
  }, [data]);

  if (!organization?.config?.showRoutineAlert) return null;
  if (isRoutinePage || !data) return null;
  return (
    <>
      {data.numberOfRoutinesEndingSoon > 0 && (
        <Alert
          className='routine-alert'
          severity='error'
          hidden={hiddenRoutinesEndingSoon}
          handleClose={() => setHiddenRoutinesEndingSoon(true)}
          hideIcon
        >
          <h5>Revisa tus rutinas</h5>
          <p>
            Tus pacientes supervisados tienen {data.numberOfRoutinesEndingSoon}{' '}
            {data.numberOfRoutinesEndingSoon === 1 ? 'rutina que necesita ser renovada' : 'rutinas que necesitan ser renovadas'}.
            Haz{' '}
            <Link className='routine-alert-link' to={'/routines?onlySupervisedUsers=true'}>
              click aquí
            </Link>{' '}
            para ver las rutinas de tus pacientes.
          </p>
        </Alert>
      )}
      {data.patientsWithoutRoutine && data.patientsWithoutRoutine.length > 0 && (
        <Alert
          className='routine-alert'
          severity='error'
          hidden={hiddenPatientsWithoutRoutine}
          handleClose={() => setHiddenPatientsWithoutRoutine(true)}
          hideIcon
        >
          <h5>Pacientes sin rutina</h5>
          <ul>
            {data.patientsWithoutRoutine.map((patient) => (
              <li key={patient.id}>
                <Link className='routine-alert-link' to={`/users/profile/${patient.id}`}>
                  {patient.fullName}
                </Link>
              </li>
            ))}
          </ul>
        </Alert>
      )}
    </>
  );
};
